import {Icon, Typography, useTheme} from '@innowise-group/ui-kit';
import {useTranslation} from 'react-i18next';
import {AttendanceStatistics, Can, RoomType, useService} from '@innowise-group/core';

import * as Styled from './room-info.styles';
import React, {useCallback, useContext, useMemo} from 'react';
import {subject} from '@casl/ability';

import {TypeRoomEnum} from '@shared/utilities/determine-room-meeting-id.utility';

import {useMeetingRoomTimer} from './use-meeting-room-timer.hook';
import {getRoomType} from '../../utilities';
import {FloorsModalsFacadeService} from '../../services/floors-modals-facade';
import {FloorContext} from '../../components/floor-plan/floor-plan.component';

import {MeetingRoomData} from '../plan-element-view';
import {MeetingRoomTooltip} from '../meeting-room-tooltip';
import {MeetingRoomStatus} from '../meeting-room-status';
import {AttendancePercentage} from '../attendance-percentage';

interface RoomInfoProps {
  id: string;
  roomType: RoomType;
  label: string;
  handlerEditRoom: () => void;
  isEditMode: boolean;
  isEditRoom: boolean;
  isOur: boolean;
  workspaceCount: number;
  freeWorkspaceCount: number;
  occupiedWorkspaceCount: number;
  meetingRoomData: MeetingRoomData | null;
  isRoomSelected: boolean;
  attendanceStatistics: AttendanceStatistics;
}

const RoomInfo: React.FC<RoomInfoProps> = ({
  id,
  roomType,
  label,
  handlerEditRoom,
  isEditMode,
  isEditRoom,
  workspaceCount,
  freeWorkspaceCount,
  occupiedWorkspaceCount,
  meetingRoomData,
  isRoomSelected,
  attendanceStatistics,
}) => {
  const {t} = useTranslation();
  const floorsModalsFacade = useService(FloorsModalsFacadeService);
  const theme = useTheme();
  const {floor} = useContext(FloorContext);
  const isMeetingRoomSelected = isRoomSelected && roomType === TypeRoomEnum.Meeting && !isEditMode;

  const {timeRemaining} = useMeetingRoomTimer(
    meetingRoomData?.meetingRoomId,
    meetingRoomData?.nextBooking || meetingRoomData?.timeWhenWillBeFree,
  );

  const colorIcon = useMemo(() => {
    return isEditRoom ? theme.palette.general.smart : theme.palette.general.darkGrey[60];
  }, [isEditRoom, theme]);

  const showMeetingRoomStatus = Boolean(
    roomType === TypeRoomEnum.Meeting && !isMeetingRoomSelected && meetingRoomData?.meetingRoomId,
  );

  const handleBookMeetingRoom = useCallback(() => {
    floorsModalsFacade.openBookingMeetingRoomForm(
      meetingRoomData?.meetingRoomId,
      t('modals.bookMeetingRoom.title'),
      floor?.fullAddress,
    );
  }, [floorsModalsFacade, meetingRoomData?.meetingRoomId, floor?.fullAddress, t]);

  const roomAttendance = attendanceStatistics?.rooms?.find((room) => room.number === label);
  const showWorkspaceStats = isRoomSelected && !!workspaceCount && !meetingRoomData?.meetingRoomId;

  return (
    <MeetingRoomTooltip
      isViewMode={!isEditMode}
      selected={isMeetingRoomSelected && !!meetingRoomData?.meetingRoomId}
      onOpenBookingModal={handleBookMeetingRoom}
      meetingRoomData={meetingRoomData}
      timeRemaining={timeRemaining}
    >
      <Styled.RoomInfoContainer>
        <Styled.RoomTypeContainer>
          <Styled.RoomTypeWrap>
            <Styled.RoomTypeText type="h3" isEditRoom={isEditRoom}>{`${label}`}</Styled.RoomTypeText>
            <Styled.RoomTypeText type="h3" isEditRoom={isEditRoom}>
              ({getRoomType(roomType, t)})
            </Styled.RoomTypeText>
          </Styled.RoomTypeWrap>
          {isEditMode && <Icon type="u_edit-alt" fill={colorIcon} size={18} onClick={handlerEditRoom} isPointer />}
        </Styled.RoomTypeContainer>

        {showMeetingRoomStatus && (
          <MeetingRoomStatus meetingRoomStatus={meetingRoomData?.isBusy} timeRemaining={timeRemaining} />
        )}

        <Styled.AttendanceInfo hasContent={showWorkspaceStats || !!roomAttendance?.attendance_percentage}>
          {showWorkspaceStats && (
            <Can I="UPDATE" this={subject('ROOM', {id})}>
              <Styled.StatsBlock>
                <Typography>{t('pages.offices.attendance.totalWorkspaces')}</Typography>
                <Typography style={{fontWeight: '700', fontSize: '16px'}}>{workspaceCount}</Typography>
              </Styled.StatsBlock>
              <Styled.StatsBlock>
                <Typography>{t('rooms.free')}</Typography>
                <Typography style={{fontWeight: '700', fontSize: '16px'}}>{freeWorkspaceCount}</Typography>
              </Styled.StatsBlock>
              <Styled.StatsBlock>
                <Typography>{t('rooms.occupied')}</Typography>
                <Typography style={{fontWeight: '700', fontSize: '16px'}}>{occupiedWorkspaceCount}</Typography>
              </Styled.StatsBlock>
            </Can>
          )}
          <Can I="READ" a="ROOM-ATTENDANCE">
            {isRoomSelected && !meetingRoomData?.meetingRoomId && (
              <AttendancePercentage
                attendancePercent={roomAttendance?.attendance_percentage ?? 0}
                attendanceType={'room'}
              />
            )}
          </Can>
        </Styled.AttendanceInfo>
      </Styled.RoomInfoContainer>
    </MeetingRoomTooltip>
  );
};

export default React.memo(RoomInfo);
